import axios from 'axios';

// Détection dynamique de l'URL de l'API
// Si on est en dev (port 3000), on tape sur le backend port 9000
// Si on est en prod (servi par le backend), on utilise le chemin relatif /api
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  
  const { hostname, port, protocol } = window.location;
  
  // Cas spécifique du développement local React
  if (port === '3000') {
    return `${protocol}//${hostname}:9000/api`;
  }
  
  // Par défaut, chemin relatif (marche pour n'importe quel port de production)
  return '/api';
};

const API_URL = getApiUrl();
console.log('--- API_URL initialized as:', API_URL);

const api = axios.create({
  baseURL: API_URL
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (data) => api.post('/auth/register', data)
};

export const newsAPI = {
  getAll: () => api.get('/news'),
  create: (formData) => api.post('/news', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/news/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/news/${id}`)
};

export const teamsAPI = {
  getAll: () => api.get('/teams'),
  create: (formData) => api.post('/teams', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/teams/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/teams/${id}`)
};

export const matchesAPI = {
  getAll: () => api.get('/matches'),
  getStandings: () => api.get('/matches/standings'),
  create: (data) => api.post('/matches', data),
  update: (id, data) => api.put(`/matches/${id}`, data),
  delete: (id) => api.delete(`/matches/${id}`)
};

export const contentAPI = {
  // News Ticker
  getTicker: () => api.get('/content/ticker'),
  createTicker: (data) => api.post('/content/ticker', data),
  updateTicker: (id, data) => api.put(`/content/ticker/${id}`, data),
  deleteTicker: (id) => api.delete(`/content/ticker/${id}`),

  // Club News
  getClubNews: () => api.get('/content/club-news'),
  createClubNews: (formData) => api.post('/content/club-news', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateClubNews: (id, formData) => api.put(`/content/club-news/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteClubNews: (id) => api.delete(`/content/club-news/${id}`),

  // Committees
  getCommittees: () => api.get('/content/committees'),
  createCommittee: (data) => api.post('/content/committees', data),
  updateCommittee: (id, data) => api.put(`/content/committees/${id}`, data),
  deleteCommittee: (id) => api.delete(`/content/committees/${id}`),

  // Committee Members
  createCommitteeMember: (formData) => api.post('/content/committee-members', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateCommitteeMember: (id, formData) => api.put(`/content/committee-members/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteCommitteeMember: (id) => api.delete(`/content/committee-members/${id}`),

  // Referee Designations
  getDesignations: () => api.get('/content/designations'),
  createDesignation: (data) => api.post('/content/designations', data),
  updateDesignation: (id, data) => api.put(`/content/designations/${id}`, data),
  deleteDesignation: (id) => api.delete(`/content/designations/${id}`),

  // Discipline Decisions
  getDiscipline: () => api.get('/content/discipline'),
  createDiscipline: (formData) => api.post('/content/discipline', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateDiscipline: (id, formData) => api.put(`/content/discipline/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteDiscipline: (id) => api.delete(`/content/discipline/${id}`),

  // Players
  getPlayers: () => api.get('/content/players'),
  createPlayer: (data) => api.post('/content/players', data),
  updatePlayer: (id, data) => api.put(`/content/players/${id}`, data),
  deletePlayer: (id) => api.delete(`/content/players/${id}`),

  // Site Settings
  getSettings: () => api.get('/content/settings'),
  updateSetting: (data) => api.put('/content/settings', data),

  // Communiques
  getCommuniques: () => api.get('/content/communiques'),
  createCommunique: (formData) => api.post('/content/communiques', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  updateCommunique: (id, formData) => api.put(`/content/communiques/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteCommunique: (id) => api.delete(`/content/communiques/${id}`),

  // Team Penalties
  getPenalties: () => api.get('/content/penalties'),
  createPenalty: (data) => api.post('/content/penalties', data),
  deletePenalty: (id) => api.delete(`/content/penalties/${id}`),

  // Penalty Motifs
  getPenaltyMotifs: () => api.get('/content/penalty-motifs'),
  createPenaltyMotif: (data) => api.post('/content/penalty-motifs', data),
  updatePenaltyMotif: (id, data) => api.put(`/content/penalty-motifs/${id}`, data),
  deletePenaltyMotif: (id) => api.delete(`/content/penalty-motifs/${id}`),

  // Live Streams
  getLiveStreams: () => api.get('/content/live-streams'),
  createLiveStream: (data) => api.post('/content/live-streams', data),
  updateLiveStream: (id, data) => api.put(`/content/live-streams/${id}`, data),
  deleteLiveStream: (id) => api.delete(`/content/live-streams/${id}`)
};

export const liveStreamsAPI = {
  getAll: () => api.get('/content/live-streams'),
  create: (data) => api.post('/content/live-streams', data),
  update: (id, data) => api.put(`/content/live-streams/${id}`, data),
  delete: (id) => api.delete(`/content/live-streams/${id}`)
};

export const mediaAPI = {
  getAll: (type) => api.get('/media', { params: { type } }),
  create: (formData) => api.post('/media', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  update: (id, formData) => api.put(`/media/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/media/${id}`)
};

export default api;

